import { SelectChangeEvent, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFreightQuote,
  getQuote,
  getUser,
  postJobsiteDetails,
  postPO,
  postSummaryData,
} from "../../api/OrderSummaryApi";
import { generateCustomerPDF, generatePDF } from "../../api/pdf";
import { AddCostObj } from "../../GlobalTypes";
import getParams from "../../hooks/useParams";
import usePersistedState from "../../hooks/usePersistedState";
import formatIntWithCommas from "../../util/formatIntWithCommas";
import priceWithZeros from "../../util/priceWithZeros";
import { UnitObj } from "../../util/quoteCartStorage";
import round from "../../util/round";
import { allDealerActAccess } from "./OrderSummary";
import {
  formatAddCostsObj,
  getCustomerTotal,
  getDealerTax,
  nonMarginGrandTotal,
} from "./orderSummary.biz";

type FreightData = {
  crateCharge?: number;
  rateQuoteCharge?: number;
  rateQuoteId?: string;
  newTotal?: boolean;
  freightQuoteError?: boolean;
  freightQuotePriceUpdateRequired?: boolean;
};

function useSummarySelector() {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const matches = useMediaQuery("(min-width:1000px)", { noSsr: true });

  const changeLogo = useMediaQuery("(min-width:700px)", { noSsr: true });

  const location = useLocation();
  const id = getParams(location.pathname);

  const historyState = location.state;

  const quoteNumber = JSON.parse(
    sessionStorage.getItem("quoteNumber") || JSON.stringify(""),
  );

  const [grandTotal, setGrandTotal] = useState("");

  const [marginInput, setMarginInput] = useState("");

  const [po, setPo] = useState("");

  const [currentPo, setCurrentPo] = useState("");

  const [fetch, setFetch] = useState(false);

  const [editPoClicked, setEditPoClicked] = useState(false);

  const [isUpdatingPo, setIsUpdatingPo] = useState(false);

  const signedInUserRole = JSON.parse(
    sessionStorage.getItem("role") || JSON.stringify(""),
  );

  const signedInUserEmail = JSON.parse(
    sessionStorage.getItem("email") || JSON.stringify(""),
  );

  const {
    data: userData,
    isFetching: isFetchingUser,
    isLoading: isLoadingUser,
  } = useQuery(
    "User",
    async () => {
      const response = await getUser(historyState.userId);
      if (response.data.signInRole === "Production") {
        const user = JSON.parse(
          sessionStorage.getItem("user") || JSON.stringify(""),
        );
        return navigate(`/dealerZone/${user}`);
      }
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: () => setFetch(true),
    },
  );

  const queryKey = fetch ? ["Cart", historyState.userId, quoteNumber] : [];

  const { isLoading, error, data, isFetching } = useQuery(
    queryKey,
    async () => {
      const response = await getQuote(historyState.userId, quoteNumber);

      const { quote } = response.data;

      if (quote.poNumber) {
        setCurrentPo(quote.poNumber);
        setPo(quote.poNumber);
      }

      let marginUsed = "";

      if (quote.status !== "Quote" && quote.status !== "Archived") {
        marginUsed = quote.margin || "";
      } else {
        marginUsed =
          userData?.otsm && userData?.otsmDefault === "Fixed Margin"
            ? userData?.fixedMargin
            : userData?.otsm && userData?.otsmDefault === "Fixed Markup"
              ? userData?.fixedMarkup
              : quote.overrideFixedMargin &&
                  userData?.fixedMargin &&
                  !quote.withMarkup
                ? quote.margin
                : quote.overrideFixedMarkup &&
                    userData?.fixedMarkup &&
                    quote.withMarkup
                  ? quote.margin
                  : userData?.fixedMargin && !quote.withMarkup
                    ? userData?.fixedMargin
                    : userData?.fixedMarkup && quote.withMarkup
                      ? userData?.fixedMarkup
                      : quote.margin
                        ? quote.margin
                        : "";
      }

      quote.overrideFixedMargin && setOverride(true);
      quote.overrideFixedMarkup && setOverrideMarkup(true);

      if (userData?.otsm && userData?.otsmDefault === "Fixed Margin") {
        setWithMarkup(false);
      } else if (userData?.otsm && userData?.otsmDefault === "Fixed Markup") {
        setWithMarkup(true);
      } else {
        quote.withMarkup && setWithMarkup(true);
      }

      marginUsed && setMarginInput(marginUsed);

      const adminCharge =
        quote.adminChargeMargined && quote.adminCharge ? quote.adminCharge : "";

      const grandTotalWithMarkup =
        userData?.otsm &&
        userData?.otsmDefault === "Fixed Markup" &&
        userData?.fixedMarkup
          ? true
          : userData?.otsm &&
              userData?.otsmDefault === "Fixed Margin" &&
              userData?.fixedMargin
            ? false
            : quote.withMarkup
              ? quote.withMarkup
              : false;

      if (quote.checkedJobsite) {
        setJobsite({
          contactName: quote.jobsiteContact || "",
          contactNumber: quote.jobsiteContactNumber || "",
          address: quote.address || "",
          city: quote.city || "",
          zip: quote.zip || "",
          state: quote.state || "",
        });
      }

      const dealerTax = getDealerTax({
        status: quote.status,
        dealerTax: userData?.dealerTax || null,
        dateOrdered: quote.dateOrdered || null,
      });

      const addCosts: AddCostObj[] = formatAddCostsObj({
        isLoading: false,
        quote,
        dealerTax: dealerTax ? true : false,
      });

      let delivery = 0;

      const hasFreight =
        quote.state !== "CA" &&
        quote.freightQuote &&
        (quote.freightQuote.rateQuoteId || quote.freightQuote.freightQuoteError)
          ? true
          : false;

      if (!quote.state || quote.state === "CA") {
        delivery = quote.deliveryCharge || 0;
      } else if (hasFreight) {
        delivery +=
          quote.freightQuote.crateCharge + quote.freightQuote.rateQuoteCharge;
      }

      calcGrandTotal(
        marginUsed,
        grandTotalWithMarkup,
        addCosts,
        false,
        dealerTax ? 0 : quote.salesTax ? quote.salesTax : 0,
        delivery,
        quote.grandTotal,
        adminCharge,
        hasFreight && userData?.crateFreightMarkup
          ? true
          : hasFreight
            ? false
            : userData?.deliveryMarkup,
        true,
        dealerTax ? true : false,
      );

      quote.customCladPrice = Number(quote.customCladPrice ?? 0);

      quote.hideCustomerPricing &&
        quote.hideCustomerPricing !== "Show All" &&
        setHideCustomerPricing(quote.hideCustomerPricing);

      quote.seriesOverride && setSeries(quote.seriesOverride);

      return { quote: response.data.quote };
    },
    { refetchOnWindowFocus: false, refetchOnMount: true, cacheTime: 0 },
  );

  const [fetchingFreight, setFetchFreight] = useState(false);

  const [freightData, setFreightData] = useState<FreightData>({});

  let freightController;

  const fetchFreight = async () => {
    // Cancel any ongoing request
    if (freightController) {
      freightController.abort();
    }
    // setFreightErr("");

    freightController = new AbortController();
    setFetchFreight(true);
    try {
      const response = await getFreightQuote(quoteNumber, {
        signal: freightController.signal,
      });
      console.log("FREIGHT DATA", response.data)
      setFreightData(response.data);
      if (response.data.newTotal) {
        queryClient.invalidateQueries("Cart");
      }
    } catch (err: any) {
      if (err.name === "CanceledError") {
        console.log("Request canceled:", err.message);
      }
      // setFreightErr(
      //   err.status && err.status === 400
      //     ? "address problem"
      //     : "problem occurred",
      // );
    } finally {
      setFetchFreight(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!data?.quote?.state || data.quote.state === "CA") return;

    fetchFreight();
  }, [isLoading]);

  const dealerTax =
    isLoading || isLoadingUser
      ? null
      : getDealerTax({
          status: data?.quote.status,
          dealerTax: userData.dealerTax || null,
          dateOrdered: data?.quote.dateOrdered || null,
        });

  const addCosts: AddCostObj[] = formatAddCostsObj({
    isLoading,
    quote: data?.quote || null,
    dealerTax: dealerTax ? true : false,
  });

  const [series, setSeries] = useState("Default");

  const [overrideFixedMargin, setOverride] = useState(false);

  const [overrideFixedMarkup, setOverrideMarkup] = useState(false);

  const [hideCustomerPricing, setHideCustomerPricing] = useState("Show All");

  const adminChargeMargined =
    !isLoading && data?.quote.adminChargeMargined && data.quote?.adminCharge
      ? true
      : false;

  const disableChanges =
    !isLoading && data?.quote.status !== "Quote" ? true : false;

  const disableMargin =
    disableChanges ||
    (!isFetchingUser &&
      userData.fixedMargin &&
      !isFetching &&
      !overrideFixedMargin);

  const disableMarkup =
    disableChanges ||
    (!isFetchingUser &&
      userData.fixedMarkup &&
      !isFetching &&
      !overrideFixedMarkup);

  var custom = (unit: UnitObj) =>
    unit.customPaintOrStain && unit.customPaintOrStain !== "";

  const [errMessageMargin, setErrMessage] = usePersistedState(
    "errMessageMargin",
    "",
  );

  const [priceWithMargin, setPriceWithMargin] = useState(true); //if true customer price was calculated with margin

  const [pdfErr, setPdfErr] = useState(false);

  const [margin, setMargin] = useState(0);

  const [withMarkup, setWithMarkup] = useState(false); //margin flase, markup true

  const [taxResults, setTaxResults] = useState([]);
  const [chosenTax, setChosenTax] = useState("");

  const handleTaxChange = (event: SelectChangeEvent<string>) =>
    setChosenTax(event.target.value);

  const [jobsite, setJobsite] = useState({
    contactName: "",
    contactNumber: "",
    city: "",
    zip: "",
    state: "",
    address: "",
  });

  const [openJobsite, setOpenJobsite] = useState(false);

  const [loadJobsite, setLoadJobsite] = useState(false);

  const [openPO, setOpenPO] = useState(false);

  const [openCustomerEmailModal, setOpen] = useState(false);

  const handleOpenCustomerEmailModal = () => {
    setOpenWarning(false);
    setOpen(true);
  };

  const handleCloseCustomerEmailModal = () => setOpen(false);

  const [openWarning, setOpenWarning] = useState(false);

  const handleOpenWarning = () => setOpenWarning(true);

  const handleCloseWarning = () => setOpenWarning(false);

  const [openRecalculatingWarning, setOpenRecalculatingWarning] = useState(false);

  const handleOpenRecalculatingWarning = () => setOpenRecalculatingWarning(true);

  const handleCloseRecalculatingWarning = () => setOpenRecalculatingWarning(false);

  const [load, setLoad] = useState(false);
  const [dealerUrl, setDealerUrl] = useState<string | null>(null);
  const [customerUrl, setCustomerUrl] = useState<string | null>(null);

  const [base64Pdf, setBase64Pdf] = useState<string | ArrayBuffer | null>("");

  const resetPDF = () => {
    setDealerUrl(null);
    setCustomerUrl(null);
    setBase64Pdf("");
  };

  const handleOpenJobsite = () => {
    setTaxResults([]);
    setChosenTax("");
    setLoadJobsite(false);
    setOpenJobsite(true);
  };

  const handleCloseJobsite = () => {
    setOpenJobsite(false);
    setLoadJobsite(false);

    setJobsite({
      contactName: data?.quote.jobsiteContact || "",
      contactNumber: data?.quote.jobsiteContactNumber || "",
      address: data?.quote.address || "",
      city: data?.quote.city || "",
      zip: data?.quote.zip || "",
      state: data?.quote.state || "",
    });
  };

  const handleOpenPO = () => {
    setOpenPO(true);
  };

  const handleClosePO = () => {
    setOpenPO(false);
    setEditPoClicked(false);
    if (data?.quote.status !== "Quote") {
      setPo(currentPo);
    }
  };

  const [modalDetailsErr, setModalDetailsErr] = useState("");

  const handleJobsiteChange = (
    e:
      | SelectChangeEvent<unknown>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setJobsite((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddress = (place: google.maps.places.PlaceResult) => {
    const formatted = place.formatted_address;
    if (!formatted) return;
    const separatedAddress = formatted.split(",");
    setJobsite((prev) => ({
      ...prev,
      address: separatedAddress[0].trim(),
      city: separatedAddress[1].trim(),
      state: separatedAddress[2].trim().split(" ")[0],
      zip: separatedAddress[2].trim().split(" ")[1],
    }));
  };

  const handleModalDetails = async () => {
    if (isFetching) return;

    setLoadJobsite(true);
    const res = await postJobsiteDetails({
      id: data?.quote._id,
      jobsite,
      chosenTax: chosenTax || null,
      currentTax: data?.quote.salesTax,
    });

    if (res.data === "Problem") {
      setModalDetailsErr("Invalid Address");
      return setLoadJobsite(false);
    }

    if (res.data.error) {
      setTaxResults([]);
      setChosenTax("");
      setModalDetailsErr("Invalid Address");
      return setLoadJobsite(false);
    }

    if (res.data.results) {
      setTaxResults(res.data.results);
      return setLoadJobsite(false);
    }

    setTaxResults([]);
    setChosenTax("");
    setModalDetailsErr("");
    setOpenJobsite(false);
    setLoadJobsite(false);
    queryClient.invalidateQueries("Cart");
    await fetchFreight();
  };

  const handlePoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPo(value);
    if (data?.quote.status === "Quote") {
      await postPO(data?.quote._id, value);
      setCurrentPo(value);
      resetPDF();
    }
  };

  const handleEditPoClick = () => {
    setEditPoClicked(true);
  };

  const handleUpdatePoClick = async (input: string) => {
    setIsUpdatingPo(true);
    setTimeout(async () => {
      await postPO(data?.quote._id, input);
      setEditPoClicked(false);
      setCurrentPo(input);
      resetPDF();
      setIsUpdatingPo(false);
    }, 1200);
  };

  const handleGenerateClick = async () => {
    if (isFetching) return;
    // if (freightErr)
    //   return alert(
    //     "Crate and Freight is missing. Please Contact (805) 644-4494",
    //   );

    setLoad(true);
    try {
      if (
        allDealerActAccess(userData.signInRole) ||
        userData.signInRole === "Admin"
      ) {
        const res1 = await generatePDF({
          poNumber: currentPo || "",
          quoteId: data?.quote._id,
          grandTotal,
          isOrder:
            data?.quote.status !== "Quote" && data?.quote.status !== "Archived",
          salesOrder: "",
          isProduction: false,
          series,
          dealerTax: dealerTax || 0,
          codOrTerms:
            data?.quote.adminCOD && data?.quote.adminCOD !== "None"
              ? data?.quote.adminCOD
              : userData.codOrTerms
                ? userData.codOrTerms
                : "",
          region: historyState.region || userData.region,
        });

        const res2 = await generateCustomerPDF({
          poNumber: currentPo || "",
          quoteId: data?.quote._id,
          grandTotal,
          isOrder:
            data?.quote.status !== "Quote" && data?.quote.status !== "Archived",
          dealerTax: dealerTax || 0,
          hidePricing: hideCustomerPricing,
          salesTax:
            !dealerTax && data?.quote.salesTax ? data?.quote.salesTax : 0,
          margin,
          withMarkup,
          addCosts,
          dealerLogo: userData.dealerLogo || "",
          deliveryMarkup: userData.deliveryMarkup ? true : false,
          crateFreightMarkup: userData.crateFreightMarkup ? true : false,
        });

        const url1 = URL.createObjectURL(res1.data);
        const url2 = URL.createObjectURL(res2.data);

        setDealerUrl(url1);
        setCustomerUrl(url2);

        let reader = new FileReader();
        reader.readAsDataURL(res1.data);
        reader.onloadend = function () {
          const base64 = reader.result;
          setBase64Pdf(base64);
        };
      } else {
        const res = await generatePDF({
          poNumber: currentPo || "",
          quoteId: data?.quote._id,
          grandTotal,
          isOrder:
            data?.quote.status !== "Quote" && data?.quote.status !== "Archived",
          salesOrder: "",
          isProduction: false,
          series,
          dealerTax: dealerTax || 0,
          codOrTerms:
            data?.quote.adminCOD && data?.quote.adminCOD !== "None"
              ? data?.quote.adminCOD
              : userData.codOrTerms
                ? userData.codOrTerms
                : "",
          region: historyState.region || userData.region,
        });

        const url = URL.createObjectURL(res.data);
        setDealerUrl(url);
        setCustomerUrl("");
        let reader = new FileReader();
        reader.readAsDataURL(res.data);
        reader.onloadend = function () {
          const base64 = reader.result;
          setBase64Pdf(base64);
        };
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setDealerUrl("");
      setCustomerUrl("");
      setPdfErr(true);
      setBase64Pdf("");
    } finally {
      setLoad(false);
    }
  };

  const calcGrandTotal = async (
    marginUsed: string,
    markup: boolean,
    addCosts: AddCostObj[],
    shouldMutate: boolean,
    salesTax: number,
    deliveryCharge: number,
    grandTotalPrice: string,
    adminCharge: number,
    deliveryMarkup: boolean,
    initialCalc: boolean,
    hasDealerTax: boolean,
  ) => {
    if (initialCalc) resetPDF();

    const GTP = grandTotalPrice || data?.quote.grandTotal;
    if ((!marginUsed || !Number(marginUsed)) && !addCosts.length) {
      setGrandTotal(
        nonMarginGrandTotal({
          grandTotal: GTP,
          salesTax,
          adminCharge,
          hasDealerTax: hasDealerTax || false,
        }),
      );

      setMargin(0);
      if (shouldMutate) {
        await postSummaryData({
          quoteId: data?.quote._id,
          margin: "",
          withMarkup,
        });
        resetPDF();
      }
      return;
    }

    const gt = getCustomerTotal({
      grandTotal: GTP,
      delivery: deliveryCharge,
      deliveryMarkup: deliveryMarkup || false,
      adminCharge,
      addCosts,
      marginUsed,
      markup,
      salesTax,
      hasDealerTax,
    });

    const roundTwoDecimal = round(gt);

    var grand = formatIntWithCommas(roundTwoDecimal);

    grand = priceWithZeros(grand);

    setGrandTotal(grand);

    markup ? setPriceWithMargin(false) : setPriceWithMargin(true);

    if (shouldMutate) {
      resetPDF();
      await postSummaryData({
        quoteId: data?.quote._id,
        margin: marginUsed,
        withMarkup: markup,
      });
    }

    marginUsed && setMargin(1 - parseFloat(marginUsed) / 100);
  };

  const handleReturn = () =>
    navigate(`/config/${id}`, {
      state: { isQuoteCreated: true, userId: historyState.userId },
    });

  const handleReturnDealerOrder = () => navigate(`/dealerZone/${id}`);

  const handleRL = () => {
    const loggedInUser = JSON.parse(
      sessionStorage.getItem("user") || JSON.stringify(""),
    );
    navigate(`/dealerZone/${loggedInUser}`);
  };

  const [openModal, setOpenModal] = useState(false);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const dealerCostWithAdminCharge =
    !isLoadingUser && !isLoading && data?.quote.adminCharge && !dealerTax
      ? formatIntWithCommas(
          round(
            parseFloat(data.quote.grandTotal.replace(/\,/g, "")) +
              data.quote.adminCharge,
          ),
        )
      : null;

  const canEditCustomCladColorAndFreightQuotePrice =
    signedInUserRole === "Admin" ||
    signedInUserEmail === "laura@agmillworks.com";

  return {
    dealerCostWithAdminCharge,
    handleClose,
    handleOpen,
    openModal,
    handleRL,
    handleReturnDealerOrder,
    handleReturn,
    calcGrandTotal,
    handleGenerateClick,
    handlePoChange,
    handleModalDetails,
    handleJobsiteChange,
    modalDetailsErr,
    handleClosePO,
    handleOpenPO,
    handleCloseJobsite,
    handleOpenJobsite,
    openPO,
    loadJobsite,
    openJobsite,
    handleTaxChange,
    taxResults,
    withMarkup,
    setWithMarkup,
    margin,
    pdfErr,
    priceWithMargin,
    errMessageMargin,
    setErrMessage,
    custom,
    disableMargin,
    disableMarkup,
    disableChanges,
    adminChargeMargined,
    hideCustomerPricing,
    setHideCustomerPricing,
    overrideFixedMargin,
    setOverrideMarkup,
    overrideFixedMarkup,
    series,
    setSeries,
    addCosts,
    dealerTax,
    isLoading,
    error,
    data,
    isFetching,
    userData,
    isLoadingUser,
    po,
    marginInput,
    setMarginInput,
    grandTotal,
    changeLogo,
    matches,
    historyState,
    jobsite,
    setOverride,
    chosenTax,
    load,
    dealerUrl,
    customerUrl,
    openCustomerEmailModal,
    handleOpenCustomerEmailModal,
    handleCloseCustomerEmailModal,
    openWarning,
    handleOpenWarning,
    handleCloseWarning,
    openRecalculatingWarning,
    handleOpenRecalculatingWarning,
    handleCloseRecalculatingWarning,
    base64Pdf,
    resetPDF,
    freightData,
    fetchingFreight,
    handleAddress,
    handleEditPoClick,
    editPoClicked,
    handleUpdatePoClick,
    isUpdatingPo,
    canEditCustomCladColorAndFreightQuotePrice
  };
}

export default useSummarySelector;
